import React from 'react';
import Tooltip from 'components/Tooltip';

const FieldRow = ({ label, desc, children }) => (
  <div className="py-4 border-b border-border last:border-0">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>{label}</span>
        {desc && <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>{desc}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  </div>
);

const NumberInput = ({ value, onChange, unit, min, max, step }) => (
  <div className="flex items-center gap-2">
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(parseFloat(e?.target?.value) || 0)}
      min={min}
      max={max}
      step={step || 1}
      className="w-28 px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 text-right"
      style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}
    />
    {unit && <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>{unit}</span>}
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
    style={{ background: checked ? 'var(--color-accent)' : undefined, opacity: checked ? 1 : 0.4 }}
    role="switch"
    aria-checked={checked}
  >
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
  </button>
);

export default function PricingTab({ prefs, onChange }) {
  const set = (key) => (val) => onChange({ ...prefs, [key]: val });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-2" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Valores predeterminados para Calculadora</h3>
        <p className="text-muted-foreground mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Estos valores pre-rellenan la Calculadora de Lotes al iniciar un nuevo cálculo</p>

        <FieldRow
          label={
            <Tooltip
              title="Margen objetivo predeterminado"
              description="Valor que pre-rellena el campo Margen Objetivo en cada nuevo lote de la calculadora."
            >
              Margen objetivo predeterminado
            </Tooltip>
          }
          desc="Pre-rellena el campo de margen en la calculadora"
        >
          <NumberInput value={prefs?.defaultTargetMargin || 80} onChange={set('defaultTargetMargin')} unit="€/t" min={0} max={500} step={5} />
        </FieldRow>

        <FieldRow
          label={
            <Tooltip
              title="Días de procesamiento predeterminados"
              description="Días por defecto usados cuando la categoría no tiene valor configurado."
            >
              Días de procesamiento predeterminados
            </Tooltip>
          }
          desc="Fallback cuando la categoría no tiene valor"
        >
          <NumberInput value={prefs?.defaultProcessingDays || 7} onChange={set('defaultProcessingDays')} unit="días" min={1} max={90} step={1} />
        </FieldRow>

        <FieldRow
          label={
            <Tooltip
              title="Coste logístico predeterminado"
              description="Coste de logística por defecto cuando la categoría no lo especifica."
            >
              Coste logístico predeterminado
            </Tooltip>
          }
          desc="Fallback cuando la categoría no tiene valor"
        >
          <NumberInput value={prefs?.defaultLogisticsCost || 30} onChange={set('defaultLogisticsCost')} unit="€/t" min={0} max={500} step={5} />
        </FieldRow>
      </div>
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-2" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>
          <Tooltip
            title="Modo de buffer de riesgo predeterminado"
            description="Modo por defecto del buffer de riesgo al abrir la calculadora."
          >
            Modo de buffer de riesgo predeterminado
          </Tooltip>
        </h3>
        <p className="text-muted-foreground mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Selecciona el modo que se usará por defecto al abrir la Calculadora de Lotes</p>
        <div className="space-y-2">
          {[
            {
              value: 'dynamic',
              label: 'Dinámico — Volatilidad histórica (recomendado)',
              desc: 'Calcula el buffer usando VaR al 95%: σ_diaria × √días × 1.65 × benchmark_EUR × yield. Se recalcula automáticamente con cada cambio de precio o tipo de cambio.',
            },
            {
              value: 'manual',
              label: 'Manual — Porcentaje fijo',
              desc: 'El usuario introduce directamente el porcentaje de buffer. Útil cuando se quiere fijar un valor específico independientemente de la volatilidad del mercado.',
            },
          ]?.map((opt) => (
            <label
              key={opt?.value}
              className="flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors"
              style={{
                borderColor: (prefs?.riskBufferMode || 'dynamic') === opt?.value ? 'var(--color-accent)' : 'var(--color-border)',
                background: (prefs?.riskBufferMode || 'dynamic') === opt?.value
                  ? 'color-mix(in srgb, var(--color-accent) 8%, transparent)' : 'var(--color-background)',
              }}
            >
              <input
                type="radio"
                name="riskBufferMode"
                value={opt?.value}
                checked={(prefs?.riskBufferMode || 'dynamic') === opt?.value}
                onChange={() => set('riskBufferMode')(opt?.value)}
                className="mt-0.5"
                style={{ accentColor: 'var(--color-accent)' }}
              />
              <div>
                <p className="text-foreground font-medium" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>{opt?.label}</p>
                <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>{opt?.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border p-6" style={{ background: 'var(--color-card)' }}>
        <h3 className="text-foreground font-semibold mb-4" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Interfaz de la Calculadora</h3>
        <FieldRow label="Mostrar campos avanzados" desc="Muestra campos adicionales en la Calculadora de Lotes por defecto">
          <Toggle checked={prefs?.showAdvancedFields !== false} onChange={set('showAdvancedFields')} />
        </FieldRow>
      </div>
    </div>
  );
}
